import useUserQueryStore from "../context/UserStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import APIClient from "../services/apiClient";
import { toCreateConfigPayload } from "../utils/ConfigNormalization";
import { replaceConfigInList, resolveSavedConfig } from "../utils/ConfigSaveMerge";

interface AddConfigContext {
  previousConfigs: MultiTenantConfigGetDto[];
}

const useAddConfig = (onAdd: (newData: MultiTenantConfigGetDto[]) => void) => {
  const queryClient = useQueryClient();
  const currentToken = useUserQueryStore((s) => s.userQuery.currentToken);
  const apiClient = new APIClient<MultiTenantConfigGetDto>("/MultiTenantConfig", currentToken!);

  return useMutation<MultiTenantConfigGetDto, Error, MultiTenantConfigGetDto, AddConfigContext>({
    mutationFn: async (newConfig) => {
      const saved = await apiClient.post(toCreateConfigPayload(newConfig) as unknown as MultiTenantConfigGetDto);
      return resolveSavedConfig(newConfig, saved);
    },
    onMutate: (newConfig) => {
      const previousConfigs = queryClient.getQueryData<MultiTenantConfigGetDto[]>(["configurations"]) || [];
      queryClient.setQueryData<MultiTenantConfigGetDto[]>(["configurations"], (configs = []) => [
        newConfig,
        ...configs,
      ]);

      return { previousConfigs };
    },
    onSuccess: (savedConfig, newConfig) => {
      queryClient.setQueryData<MultiTenantConfigGetDto[]>(["configurations"], (configs) =>
        replaceConfigInList(configs, newConfig, savedConfig)
      );

      const newConfigs = queryClient.getQueryData<MultiTenantConfigGetDto[]>(["configurations"]) || [];
      onAdd(newConfigs);
    },
    onError: (_error, _newConfig, context) => {
      if (!context) return;

      queryClient.setQueryData<MultiTenantConfigGetDto[]>(["configurations"], context.previousConfigs);
    },
  });
};

export default useAddConfig;
